import { useMemo } from 'react';
import createContext from 'constate';
import { useZoneApi } from '../../hooks/useZoneApi';
import { useQueryGetTableMapSummary } from '../../hooks/useTableMapApi';
import type { TableMapSummaryDto } from '@ordr-as/ordr-service-types/epos';

const EMPTY_SUMMARIES: TableMapSummaryDto[] = [];

interface AvailabilityProps {
  locationId: number;
  activeOnly?: boolean;
  excludeEmptyMaps?: boolean;
}

interface AvailabilityState {
  zoneId?: number;
  tableMapSummaries: TableMapSummaryDto[];
  isTableMapEnabled: boolean;
  isLoadingAvailability: boolean;
}

const useTableMapAvailabilityValue = ({
  locationId,
  activeOnly = true,
  excludeEmptyMaps = true,
}: AvailabilityProps): AvailabilityState => {
  const { data: zoneResponse, isPending: isLoadingZone } = useZoneApi(locationId, false);
  const zoneId = zoneResponse?.zones[0]?.id;

  const { data: tableMapSummaryResponse, isPending: isLoadingSummary } = useQueryGetTableMapSummary(
    locationId,
    zoneId,
    activeOnly,
    excludeEmptyMaps
  );

  const summaries = useMemo(
    () => tableMapSummaryResponse?.tableMapSummaries ?? EMPTY_SUMMARIES,
    [tableMapSummaryResponse?.tableMapSummaries]
  );
  const isTableMapEnabled = summaries.length > 0;
  const isLoadingAvailability = isLoadingZone || (Boolean(zoneId) && isLoadingSummary);

  return useMemo(
    () => ({
      isLoadingAvailability,
      isTableMapEnabled,
      tableMapSummaries: summaries,
      zoneId,
    }),
    [zoneId, summaries, isTableMapEnabled, isLoadingAvailability]
  );
};

const [TableMapAvailabilityProvider, useTableMapAvailability] = createContext(useTableMapAvailabilityValue);

export { TableMapAvailabilityProvider, useTableMapAvailability };
export { useTableMapAvailabilityValue };
